"use client"

import { BlockchainIcon } from "@/app/blockchains/blockchain-icon/blockchain-icon"
import { BlockchainsMoleculeReatom } from "@/app/blockchains/blockchain-molecule.reatom"
import { BlockchainView } from "@/app/blockchains/blockchain-view"
import { Copy } from "@/app/components/copy/copy"
import { WalletAddressLink } from "@/app/components/wallet-address"
import { ErrorMessage } from "@/app/error"
import { ShareIcon } from "@/app/icons/share"
import { VerifiedIcon } from "@/app/icons/verified"
import {
  ItemInfoSection,
  ItemInfoSkeleton,
} from "@/app/item/view/item-info-section"
import { getCollectionHref } from "@/app/navigation/collection"
import { isExternalListing } from "@anaconda/core/nft/listing-source"
import { Trans } from "@lingui/react/macro"
import { Platform } from "@rarible/api-client"
import { Button } from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { useMolecule } from "bunshi/react"
import Link from "next/link"
import React, { Suspense } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { useUserOwnedNft } from "../hooks/use-user-owned-nft"
import { useNftReatom } from "../store/hooks.reatom"
import { RefreshMetaButtonReatom } from "./refresh-meta-button.reatom"
import { TransferItemButtonReatom } from "./transfer-item-button.reatom"
import { useItemFullUrl } from "./use-item-full-url"

export function NftInfoSectionReatom() {
  return (
    <ErrorBoundary fallback={<ErrorMessage />}>
      <Suspense fallback={<ItemInfoSkeleton />}>
        <NftInfoSectionContentReatom />
      </Suspense>
    </ErrorBoundary>
  )
}

const NftInfoSectionContentReatom = reatomComponent(() => {
  const nft = useNftReatom()
  const collectionHref = getCollectionHref(nft.collection.id)
  const fullCollectionUrl = useItemFullUrl(nft.id)
  const { reatomBlockchainChainName } = useMolecule(BlockchainsMoleculeReatom)
  const ownershipCheck = useUserOwnedNft({ owner: nft.owner })

  // Call factory function and then call the resulting function with blockchain parameter
  const blockchainChainName = reatomBlockchainChainName()(
    nft.collection.blockchain,
  )

  const heading = (
    <div className="flex items-center gap-2 overflow-hidden text-nowrap text-brand">
      <Link
        className="flex items-center gap-2 overflow-hidden text-nowrap text-brand"
        href={collectionHref}
      >
        <span className="overflow-hidden text-ellipsis">
          {nft.collection.name}
        </span>
        {nft.collection.verified && (
          <VerifiedIcon className="h-[15px] w-[15px]" />
        )}
      </Link>
    </div>
  )

  const actions = (
    <div className="flex items-center gap-[10px]">
      {ownershipCheck.userConnected && ownershipCheck.userOwnedNft && (
        <TransferItemButtonReatom />
      )}
      <RefreshMetaButtonReatom />
      <Copy copyValue={fullCollectionUrl} label="Copy Link" asChild>
        <Button variant="ghost" size="icon">
          <ShareIcon className="[&&]:h-6 [&&]:w-6" />
        </Button>
      </Copy>
    </div>
  )

  const title = nft.name
  // Union also serves other marketplaces' orders (e.g. OpenSea Seaport) as the
  // best sell order without a listingSource, so check the order's platform too.
  const orderPlatform = nft.bestSellOrder?.platform
  const listedOnRarible =
    Boolean(nft.price) &&
    !isExternalListing(nft.listingSource) &&
    (!orderPlatform || orderPlatform === Platform.RARIBLE)

  const subtitle =
    nft.owner || listedOnRarible ? (
      <div className="flex flex-wrap items-center gap-2">
        {nft.owner && (
          <div className="flex items-center gap-2">
            <span className="text-foreground/60">
              <Trans>Owned by</Trans>
            </span>
            <WalletAddressLink data-testid="nft-owner" address={nft.owner} />
            <span className="text-foreground/60">on</span>
            <BlockchainView
              icon={<BlockchainIcon blockchain={nft.collection.blockchain} />}
              name={blockchainChainName}
            />
          </div>
        )}
        {listedOnRarible && (
          <span className="text-foreground/60 md:ml-auto">
            <Trans>
              Listed on <span className="text-white">RARIBLE</span>
            </Trans>
          </span>
        )}
      </div>
    ) : null

  return (
    <ItemInfoSection
      heading={heading}
      actions={actions}
      title={title}
      subtitle={subtitle}
    />
  )
}, "NftInfoSectionContentReatom")
